// TablaRequerimientos.jsx
import { useCallback, useState } from "react";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { traerSalones } from "../../../../store/cotizaciones/thunkCotizaciones";
import { ExtraerArticulo, obtenerListaArticulosSegunGrupo } from "../../../../store/Articulos/thunkArticulos";
import { EditarArticulo } from "../../Articulos/EditarArticulo";
import { buildArticuloOptionsByHotel } from "./articulosHotelUtils";

/**
 * Objeto base de un requerimiento, sirve como plantilla para crear nuevos registros.
 */
const requerimientoVacio = {
    id: 1,
    hotel: "",
    hora: "",
    fecha: [],
    cantidad: 1,
    articulos: "",
    descripcionArticulo: "",
    dias: 0, // Calculado según las fechas seleccionadas
    salon: [],
    precio: "",
    subtotal: "",
    articulosOptions: [],
    salonOptions: [],
};

/**
 * Formatea un número a dos decimales.
 */
const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * Limpia un subtotal en formato string, extrayendo solo valores numéricos.
 */
const limpiarSubtotal = (valor) => parseFloat((valor || "").toString().replace(/[^0-9.]/g, "")) || 0;

/**
 * Calcula los totales (subtotal, impuestos y total ajustado) a partir del estado actual del formulario.
 */
const calcularTotales = (requerimientos) => {
    let totalGlobal = 0;
    requerimientos.forEach((item) => {
        totalGlobal += limpiarSubtotal(item.subtotal);
    });

    const subtotal = totalGlobal;
    const impuestoVenta = (13 / 100) * totalGlobal;
    const impuestoServicio = (10 / 100) * totalGlobal;
    const totalAB = subtotal + impuestoVenta + impuestoServicio;

    return {
        subtotal: formatter.format(subtotal),
        impuestoVenta: formatter.format(impuestoVenta),
        impuestoServicio: formatter.format(impuestoServicio),
        totalAB: formatter.format(totalAB),
    };
};

/**
 * Recalcula la cantidad de días de un requerimiento, contando solo las fechas no vacías.
 */
const recalcularDias = (requerimiento) => requerimiento.fecha.filter((f) => f !== "").length;

/**
 * Dado un requerimiento, recalcula el subtotal en base a dias * cantidad * precio.
 */
const recalcularSubtotalRequerimiento = (req) => {
    const dias = parseFloat(req.dias || 0);
    const cantidad = parseFloat(req.cantidad || 0);
    const precio = parseFloat(req.precio || 0);
    const subtotal = dias * cantidad * precio;
    return formatter.format(subtotal);
};

const formatArticuloOptionLabel = (option, { context }) => {
    if (context === "value") {
        return option.label;
    }

    return (
        <div>
            <div>{option.label}</div>
            <div style={{ color: "#64748b", fontSize: "11px", marginTop: "2px" }}>{option.hotelNamesText}</div>
        </div>
    );
};

/**
 * Componente principal. Muestra y gestiona la tabla de requerimientos.
 */
export const TablaRequerimientos = ({ formulario, setFormulario, hotelesOpciones }) => {
    const dispatch = useDispatch();
    const [lineCounter, setLineCounter] = useState(1);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedArticulo, setSelectedArticulo] = useState(null);

    /**
     * Obtiene la lista de salones para un hotel.
     */
    const fetchSalonesAPI = useCallback(
        async (hotelValue) => {
            const salones = await dispatch(traerSalones({ idHotel: hotelValue }));
            return salones.map((salon) => ({ value: salon.id_salon, label: salon.nombre_salon }));
        },
        [dispatch],
    );

    /**
     * Obtiene la lista completa de artículos.
     * Podrías adaptarlo para filtrar según el hotel, si fuese necesario.
     */
    const fetchArticulosAPI = useCallback(async (hotelSeleccionado) => {
        const articulos = await dispatch(obtenerListaArticulosSegunGrupo(1, "AYB"));
        return buildArticuloOptionsByHotel(articulos, hotelSeleccionado);
    }, [dispatch]);

    /**
     * Agrega un nuevo requerimiento, obteniendo las opciones de salones y artículos.
     */
    const agregarRequerimiento = async () => {
        const newArticulosOptions = await fetchArticulosAPI(formulario.hotelSeleccionado);
        const newSalonesOptions = await fetchSalonesAPI(formulario.hotelSeleccionado);

        const nuevoReq = {
            ...requerimientoVacio,
            id: lineCounter + 1,
            hotel: `${formulario.hotelSeleccionado}` || "",
            salonOptions: newSalonesOptions,
            articulosOptions: newArticulosOptions,
        };

        setFormulario((prev) => ({
            ...prev,
            requerimientos: [...prev.requerimientos, nuevoReq],
        }));
        setLineCounter((count) => count + 1);
    };

    /**
     * Dado el ID de un artículo y el hotel seleccionado, actualiza la descripción, precio y subtotal.
     */
    const actualizarCamposSegunArticulo = async (index, idArticulo, hotelSeleccionado) => {
        try {
            const articulo = await dispatch(ExtraerArticulo(idArticulo));
            let precioFinal = articulo.precios_especificos?.[hotelSeleccionado] || articulo.precio_art;

            if (!articulo.precios_especificos?.[hotelSeleccionado]) {
                alert("No se encontró un precio específico para el hotel seleccionado. Se usará el precio base.");
            }

            // Actualizamos el requerimiento con la descripción y el precio
            setFormulario((prev) => {
                const reqs = [...prev.requerimientos];
                reqs[index] = {
                    ...reqs[index],
                    descripcionArticulo: articulo.desc_articulo,
                    precio: precioFinal,
                    subtotal: recalcularSubtotalRequerimiento({ ...reqs[index], precio: precioFinal }),
                };

                return {
                    ...prev,
                    requerimientos: reqs,
                    totalRequerimientos: calcularTotales(reqs),
                };
            });
        } catch (error) {
            console.error("Error al actualizar descripción del artículo:", error);
        }
    };

    /**
     * Actualiza uno o varios campos de un requerimiento.
     * `campo` puede ser un string o un objeto con múltiples pares clave-valor.
     */
    const actualizarCampoRequerimiento = (index, campo, valor) => {
        setFormulario((prev) => {
            const reqs = [...prev.requerimientos];

            // Actualiza el requerimiento según si campo es string u objeto
            if (typeof campo === "string") {
                reqs[index] = { ...reqs[index], [campo]: valor };
            } else {
                reqs[index] = { ...reqs[index], ...campo };
            }

            // Recalcula subtotal si es necesario
            reqs[index].subtotal = recalcularSubtotalRequerimiento(reqs[index]);

            return {
                ...prev,
                requerimientos: reqs,
                totalRequerimientos: calcularTotales(reqs),
            };
        });
    };

    /**
     * Elimina un requerimiento por índice.
     */
    const eliminarRequerimiento = (index) => {
        setFormulario((prev) => {
            const reqs = prev.requerimientos.filter((_, i) => i !== index);
            return {
                ...prev,
                requerimientos: reqs,
                totalRequerimientos: calcularTotales(reqs),
            };
        });
    };
   const clonarLinea = (datos, index) => {
       // Crea un nuevo objeto 'nuevoReq' clonando el objeto 'datos' recibido
       // y asigna un nuevo id basado en el índice recibido más 1.
       const nuevoReq = {
           ...datos, // Copia las propiedades de 'datos'
           id: index + 1, // Asigna un nuevo id único
       };

       // Actualiza el estado del formulario utilizando la función setFormulario.
       setFormulario((prev) => {
           // Crea un nuevo array de requerimientos que incluye los anteriores
           // más el nuevo requerimiento creado.
           const nuevosRequerimientos = [...prev.requerimientos, nuevoReq];

           // Retorna el nuevo estado del formulario con los requerimientos actualizados
           // y recalcula el total de requerimientos utilizando la función 'calcularTotales'.
           return {
               ...prev, // Copia las propiedades del estado actual
               requerimientos: nuevosRequerimientos, // Actualiza la lista de requerimientos
               totalRequerimientos: calcularTotales(nuevosRequerimientos), // Calcula y actualiza el total
           };
       });

       // Incrementa el contador de líneas en 2 unidades y actualiza su estado.
       setLineCounter((count) => count + 2);
   };


    /**
     * Cambia el hotel de un requerimiento, actualizando salones y artículos.
     */
    const handleHotelChange = async (index, selectedOption) => {
        try {
            const hotelValue = `${selectedOption?.value || ""}`;
            const newArticulosOptions = await fetchArticulosAPI(hotelValue);
            const newSalonesOptions = await fetchSalonesAPI(hotelValue);

            actualizarCampoRequerimiento(index, {
                hotel: hotelValue,
                articulos: "",
                descripcionArticulo: "",
                salon: [],
                precio: 0,
                subtotal: 0,
                articulosOptions: newArticulosOptions,
                salonOptions: newSalonesOptions,
            });
        } catch (error) {
            console.error("Error al cambiar de hotel:", error);
        }
    };

    /**
     * Agrega una fecha vacía a un requerimiento.
     */
    const agregarFecha = (reqIndex) => {
        setFormulario((prev) => {
            const reqs = [...prev.requerimientos];
            reqs[reqIndex].fecha = [...reqs[reqIndex].fecha, ""];
            reqs[reqIndex].dias = recalcularDias(reqs[reqIndex]);
            reqs[reqIndex].subtotal = recalcularSubtotalRequerimiento(reqs[reqIndex]);

            return {
                ...prev,
                requerimientos: reqs,
                totalRequerimientos: calcularTotales(reqs),
            };
        });
    };

    /**
     * Actualiza una fecha individual de un requerimiento y recalcula días y subtotal.
     */
    const actualizarFechaIndividual = (reqIndex, fechaIndex, newValue) => {
        setFormulario((prev) => {
            const reqs = [...prev.requerimientos];
            reqs[reqIndex].fecha[fechaIndex] = newValue;
            reqs[reqIndex].dias = recalcularDias(reqs[reqIndex]);
            reqs[reqIndex].subtotal = recalcularSubtotalRequerimiento(reqs[reqIndex]);

            return {
                ...prev,
                requerimientos: reqs,
                totalRequerimientos: calcularTotales(reqs),
            };
        });
    };

    /**
     * Elimina una fecha específica de un requerimiento.
     */
    const eliminarFecha = (reqIndex, fechaIndex) => {
        setFormulario((prev) => {
            const reqs = [...prev.requerimientos];
            reqs[reqIndex].fecha.splice(fechaIndex, 1);
            reqs[reqIndex].dias = recalcularDias(reqs[reqIndex]);
            reqs[reqIndex].subtotal = recalcularSubtotalRequerimiento(reqs[reqIndex]);

            return {
                ...prev,
                requerimientos: reqs,
                totalRequerimientos: calcularTotales(reqs),
            };
        });
    };

    /**
     * Cierra el modal de edición de artículo.
     */
    const handleCloseEditModal = () => {
        setSelectedArticulo(null);
        setOpenEditModal(false);
    };

    /**
     * Abre el modal de edición de un artículo seleccionado.
     */
    const mostrarIdArticulo = async (idArticulo) => {
        if (!idArticulo) {
            alert("Seleccione un artículo para editar");
            return;
        }
        const articulo = await dispatch(ExtraerArticulo(idArticulo));
        setSelectedArticulo(articulo);
        setOpenEditModal(true);
    };

    return (
        <div className="col-12">
            <div style={{ textAlign: "center", backgroundColor: "#000", color: "#fff" }} className="alert alert-secondary" role="alert">
                REQUERIMIENTO DE EVENTO
            </div>
            <div className="table-responsive" style={{ overflowX: "auto", width: "100%" }}>
                <table className="table table-hover mb-0" style={{ minWidth: "1300px" }}>
                    <thead>
                        <tr>
                            <th>Seleccione Hotel</th>
                            <th>Horas Evento</th>
                            <th>Fechas Evento</th>
                            <th>Cant.</th>
                            <th>Seleccionar Artículo</th>
                            <th>Descripción de Artículo</th>
                            <th>Días</th>
                            <th>Seleccionar Salones</th>
                            <th>Precio Artículo</th>
                            <th>Subtotal</th>
                            <th>Eliminar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formulario.requerimientos.map((req, index) => (
                            <tr key={req.id}>
                                {/* Selección de Hotel */}
                                <td style={{ minWidth: 210 }}>
                                    <Select
                                        options={[...hotelesOpciones, { value: "200", label: "Precio estandar" }]}
                                        value={
                                            req.hotel === "200"
                                                ? { value: "200", label: "Precio estandar" }
                                                : hotelesOpciones.find((opt) => opt.value === req.hotel) || null
                                        }
                                        onChange={(selected) => handleHotelChange(index, selected)}
                                        placeholder="Seleccione hotel"
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </td>

                                {/* Horas Evento */}
                                <td>
                                    <textarea
                                        className="form-control form-control-sm"
                                        placeholder="Hora"
                                        rows={2}
                                        style={{ width: "150px", marginBottom: "10px" }}
                                        value={req.hora}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "hora", e.target.value)}
                                    />
                                </td>

                                {/* Fechas Evento */}
                                <td>
                                    {req.fecha.map((fechaVal, fIndex) => (
                                        <div key={fIndex} className="d-flex align-items-center mb-2">
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaVal}
                                                onChange={(e) => actualizarFechaIndividual(index, fIndex, e.target.value)}
                                            />
                                            <button type="button" className="btn btn-danger btn-sm ms-2" onClick={() => eliminarFecha(index, fIndex)}>
                                                -
                                            </button>
                                        </div>
                                    ))}

                                    <button type="button" className="btn btn-primary btn-sm" onClick={() => agregarFecha(index)}>
                                        + Agregar Fecha
                                    </button>
                                </td>

                                {/* Cantidad */}
                                <td>
                                    <input
                                        style={{ minWidth: 80 }}
                                        type="number"
                                        className="form-control"
                                        value={req.cantidad}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "cantidad", e.target.value)}
                                    />
                                </td>

                                {/* Seleccionar Artículo */}
                                <td style={{ minWidth: 260, position: "relative" }}>
                                    <Select
                                        options={req.articulosOptions}
                                        value={req.articulosOptions.find((opt) => opt.value === req.articulos) || null}
                                        formatOptionLabel={formatArticuloOptionLabel}
                                        onChange={(selectedOption) => {
                                            const value = selectedOption?.value || "";
                                            actualizarCampoRequerimiento(index, "articulos", value);
                                            actualizarCamposSegunArticulo(index, value, req.hotel);
                                        }}
                                        placeholder="Seleccione un artículo"
                                        menuPortalTarget={document.body}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-link position-absolute"
                                        style={{ top: -10, right: 5, fontSize: "1.2rem", color: "#007bff" }}
                                        onClick={() => mostrarIdArticulo(req.articulos)}
                                    >
                                        🖉
                                    </button>
                                </td>

                                {/* Descripción Artículo */}
                                <td>
                                    <textarea
                                        className="form-control form-control-sm"
                                        placeholder="Descripción"
                                        rows={10}
                                        style={{ width: "248px", resize: "both" }}
                                        value={req.descripcionArticulo}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "descripcionArticulo", e.target.value)}
                                    />
                                </td>

                                {/* Días */}
                                <td>
                                    <input
                                        style={{ minWidth: 80 }}
                                        type="number"
                                        className="form-control"
                                        value={req.dias}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "dias", e.target.value)}
                                    />
                                </td>

                                {/* Seleccionar Salones */}
                                <td style={{ minWidth: 230 }}>
                                    <Select
                                        isMulti
                                        options={req.salonOptions}
                                        value={req.salonOptions.filter((opt) => req.salon.includes(opt.value))}
                                        onChange={(selectedOptions) => {
                                            const values = selectedOptions.map((opt) => opt.value);
                                            actualizarCampoRequerimiento(index, "salon", values);
                                        }}
                                        placeholder="Seleccione salón(es)"
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </td>

                                {/* Precio Artículo */}
                                <td>
                                    <input
                                        style={{ minWidth: 110 }}
                                        type="text"
                                        className="form-control"
                                        value={req.precio}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "precio", e.target.value)}
                                    />
                                </td>

                                {/* Subtotal */}
                                <td>
                                    <input
                                        style={{ minWidth: 110 }}
                                        type="text"
                                        className="form-control"
                                        value={req.subtotal}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "subtotal", e.target.value)}
                                    />
                                </td>

                                {/* Eliminar Requerimiento */}
                                <td>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        style={{ marginBottom: "10px", display: "block" }} // Espaciado y alineación en bloque
                                        onClick={() => eliminarRequerimiento(index)}
                                    >
                                        <i className="ti ti-trash"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        style={{ display: "block" }} // Asegura que los botones no estén alineados horizontalmente
                                        onClick={() => clonarLinea(req, index)}
                                    >
                                        <i className="ti ti-copy"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="text-end mt-3">
                <button type="button" className="btn btn-dark" onClick={agregarRequerimiento}>
                    + Agregar Requerimiento
                </button>
                <hr className="mb-2 mt-3" />
            </div>

            {selectedArticulo && (
                <EditarArticulo
                    open={openEditModal}
                    handleClose={handleCloseEditModal}
                    fetchArticulos={fetchArticulosAPI}
                    type="lista"
                    Articulo={selectedArticulo}
                />
            )}
        </div>
    );
};
